import { Card, CardHeader, CardTitle, CardContent } from '@airclou/ui';
import { UsageTrendData } from '../../api/overview';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

export interface UsageChartProps {
  data: UsageTrendData[];
  title?: string;
}

export function UsageChart({ data, title }: UsageChartProps) {
  const { t } = useTranslation('dashboard');
  // Format data for the chart
  const apiCallsLabel = t('usageChart.apiCalls');
  const storageLabel = t('usageChart.storageGb');
  const chartData = data.map(item => ({
    date: format(parseISO(item.date), 'MMM dd'),
    [apiCallsLabel]: item.apiCalls,
    [storageLabel]: item.storage,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title || t('usageChart.defaultTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            {t('usageChart.noData')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
              <XAxis 
                dataKey="date" 
                className="text-xs text-gray-600 dark:text-gray-400"
              />
              <YAxis className="text-xs text-gray-600 dark:text-gray-400" />
              <Tooltip 
                isAnimationActive={false}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.5rem',
                  color: 'hsl(var(--popover-foreground))',
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={apiCallsLabel} 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey={storageLabel} 
                stroke="#8b5cf6" 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
